import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const supported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const PushNotifyCard = () => {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const isIOS =
    typeof navigator !== "undefined" &&
    (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
  const inStandalone =
    typeof window !== "undefined" &&
    (window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches);

  useEffect(() => {
    if (!supported) {
      setReady(true);
      return;
    }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setEnabled(!!sub))
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const enable = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications were blocked. Enable them in your device settings.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setEnabled(true);
      toast.success("Push notifications enabled 🎉");
    } catch (e) {
      toast.error("Could not enable notifications. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { subscription: sub.toJSON() }).catch(() => {});
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success("Push notifications turned off.");
    } catch (e) {
      toast.error("Could not turn off notifications.");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Test sent — check your notifications!");
    } catch (e) {
      toast.error("Could not send a test notification.");
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return null;

  const iosNeedsInstall = isIOS && !inStandalone;

  return (
    <div
      data-testid="push-notify-card"
      className="mt-4 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8"
    >
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#00E676]/10">
          <Bell className="h-5 w-5 text-[#00E676]" />
        </span>
        <div className="flex-1">
          <h3 className="font-display text-xl font-semibold">Get paid? Get pinged.</h3>
          <p className="mt-1 leading-relaxed text-zinc-400">
            Turn on push notifications and your phone buzzes the instant someone tips you.
          </p>

          {!supported && (
            <p className="mt-4 text-sm text-amber-400" data-testid="push-unsupported">
              Your current browser doesn't support push notifications.
            </p>
          )}

          {supported && iosNeedsInstall && (
            <p className="mt-4 text-sm text-amber-400" data-testid="push-ios-hint">
              On iPhone, first add TipNu to your Home Screen (Share → "Add to Home Screen"),
              open it from that icon, then turn notifications on here.
            </p>
          )}

          {supported && (
            <div className="mt-5 flex flex-wrap gap-3">
              {!enabled ? (
                <button
                  data-testid="enable-push-btn"
                  onClick={enable}
                  disabled={busy || iosNeedsInstall}
                  className="inline-flex items-center gap-2 rounded-full bg-[#00E676] px-6 py-3 text-sm font-bold text-black transition-transform active:scale-95 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                  Enable notifications
                </button>
              ) : (
                <>
                  <button
                    data-testid="test-push-btn"
                    onClick={sendTest}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-full bg-[#00E676] px-6 py-3 text-sm font-bold text-black transition-transform active:scale-95 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send test
                  </button>
                  <button
                    data-testid="disable-push-btn"
                    onClick={disable}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-bold text-white hover:bg-white/5 disabled:opacity-50"
                  >
                    <BellOff className="h-4 w-4" /> Turn off
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
